import React, { type Ref } from 'react';
import investoroLogo from 'assets/icons/investoro_pdf_logo.png';
import dayjs from 'dayjs';
import styled from 'styled-components';
import type { ValuationResultDto } from 'utils/types/AiPredictorModels';

import ValuationAiSummary from 'components/valuation/sections/ValuationAiSummary';
import ValuationHeroCard from 'components/valuation/sections/ValuationHeroCard';
import ValuationLocation from 'components/valuation/sections/ValuationLocation';
import ValuationMarketContext from 'components/valuation/sections/ValuationMarketContext';
import ValuationMarketProof from 'components/valuation/sections/ValuationMarketProof';
import ValuationPlanB from 'components/valuation/sections/ValuationPlanB';

import { PdfWrapperV2 } from './ValuationStaticElements';

const PdfPage = styled.div`
    width: 1000px;
    min-height: 1413px;
    background: #ffffff;
    padding: 48px 56px 40px;
    display: flex;
    flex-direction: column;
    --section-card-bg: transparent;
    --section-card-radius: 0;
    --section-card-padding: 32px 0;
    --section-card-padding-mobile: 20px 0;
    --section-card-divider: 1px solid var(--color-alt-second);
    --report-gap: 0;
`;

const PdfTableSection = styled.div`
    padding-top: 40px;
    margin-bottom: 80px;
`;

const LargeHeaderWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 0 28px;
    margin-bottom: 28px;
`;

const LargeHeaderLogo = styled.img`
    height: 56px;
    width: auto;
`;

const LargeHeaderRight = styled.div`
    text-align: right;
`;

const LargeHeaderTitle = styled.div`
    font-size: 22px;
    font-weight: 700;
    color: var(--color-primary);
`;

const LargeHeaderMeta = styled.div`
    font-size: 12px;
    color: var(--color-primary-70);
    font-weight: 500;
    margin-top: 4px;
`;

const PdfLargeHeader = () => <LargeHeaderWrapper>
    <LargeHeaderLogo src={investoroLogo} alt="Investoro logo" />
    <LargeHeaderRight>
        <LargeHeaderTitle>Wycena ceny ofertowej</LargeHeaderTitle>
        <LargeHeaderMeta>{dayjs().format('DD.MM.YYYY')} | www.investoro.pl</LargeHeaderMeta>
    </LargeHeaderRight>
</LargeHeaderWrapper>;

const PageBarWrapper = styled.div<{ position: 'top' | 'bottom' }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    ${({ position }) => position === 'top'
        ? 'border-bottom: 1px solid var(--color-alt-second); padding: 0 0 14px; margin-bottom: 32px;'
        : 'border-top: 1px solid var(--color-alt-second); padding: 14px 0 0; margin-top: auto;'}
`;

const PageBarLogo = styled.img`
    height: 28px;
    width: auto;
`;

const PageBarText = styled.span`
    font-size: 11px;
    color: var(--color-primary-70);
    font-weight: 500;
`;

const PdfPageBar = ({ pageNumber, position }: { pageNumber: number; position: 'top' | 'bottom' }) => <PageBarWrapper position={position}>
    <PageBarLogo src={investoroLogo} alt="Investoro logo" />
    <PageBarText>{dayjs().format('DD.MM.YYYY')} | www.investoro.pl</PageBarText>
    <PageBarText>{String(pageNumber).padStart(2, '0')}</PageBarText>
</PageBarWrapper>;

const HeroCardWrapper = styled.div`
    border: 1px solid var(--color-alt-second);
    border-radius: var(--box-border-radius);
    overflow: hidden;
    margin-bottom: 32px;
`;

const MapImage = styled.img`
    width: 100%;
    height: 420px;
    object-fit: cover;
    object-position: center;
    border-radius: var(--box-border-radius);
    display: block;
    margin-top: 16px;
`;

const NotesSection = styled.div`
    padding: 32px 0 0;
    border-top: 1px solid var(--color-alt-second);
`;

const NotesTitle = styled.h2`
    font-size: 16px;
    font-weight: 600;
    color: var(--color-primary);
    margin: 0 0 24px;
`;

const NotesLines = styled.div`
    display: flex;
    flex-direction: column;
    gap: 28px;
`;

const NotesLine = styled.div`
    border-bottom: 1px dashed var(--color-alt-second);
    height: 1px;
`;

const PdfNotesSection = () => <NotesSection>
    <NotesTitle>Notatki</NotesTitle>
    <NotesLines>
        {Array.from({ length: 5 }).map((_, i) => <NotesLine key={i} />)}
    </NotesLines>
</NotesSection>;

const cleanAddress = (address: string | undefined) => address?.replace(/\s*\(Twoje dane\)/gi, '').trim();

interface ValuationPdfPagesProps {
    firstPageRef: Ref<HTMLDivElement>;
    secondPageRef: Ref<HTMLDivElement>;
    thirdPageRef: Ref<HTMLDivElement>;
    valuationResult: ValuationResultDto;
    subjectAddress?: string;
    mapImageUrl?: string;
}

const ValuationPdfPages = ({ firstPageRef, secondPageRef, thirdPageRef, valuationResult, subjectAddress, mapImageUrl }: ValuationPdfPagesProps) => {
    return <PdfWrapperV2>
        <PdfPage ref={firstPageRef}>
            <PdfLargeHeader />
            <HeroCardWrapper>
                <ValuationHeroCard
                    hero={valuationResult.heroSection}
                    subjectAddress={cleanAddress(subjectAddress)}
                    isPdf
                />
            </HeroCardWrapper>
            {valuationResult.marketProofSection &&
                <ValuationMarketProof
                    section={valuationResult.marketProofSection}
                    hideMap
                    hideTable
                    isPdf
                />
            }
            {mapImageUrl && <MapImage src={mapImageUrl} alt="Mapa ofert" />}
            <PdfPageBar pageNumber={1} position="bottom" />
        </PdfPage>

        <PdfPage ref={secondPageRef}>
            <PdfPageBar pageNumber={2} position="top" />
            {valuationResult.marketProofSection &&
                <PdfTableSection>
                    <ValuationMarketProof
                        section={valuationResult.marketProofSection}
                        tableOnly
                        isPdf
                    />
                </PdfTableSection>
            }
            {valuationResult.marketContextSection &&
                <ValuationMarketContext section={valuationResult.marketContextSection} />
            }
            <PdfPageBar pageNumber={2} position="bottom" />
        </PdfPage>

        <PdfPage ref={thirdPageRef}>
            <PdfPageBar pageNumber={3} position="top" />
            {valuationResult.planBSection &&
                <ValuationPlanB section={valuationResult.planBSection} />
            }
            {valuationResult.locationSection &&
                <ValuationLocation section={valuationResult.locationSection} />
            }
            {valuationResult.aiSummarySection &&
                <ValuationAiSummary section={valuationResult.aiSummarySection} />
            }
            <PdfNotesSection />
            <PdfPageBar pageNumber={3} position="bottom" />
        </PdfPage>

    </PdfWrapperV2>;
};

export default ValuationPdfPages;
